import React, { useEffect, useRef } from "react";
import { t } from "ttag";
import * as echarts from "echarts";

import { getDefaultSize, getMinSize } from "metabase/visualizations/shared/utils/sizes";

import type { VisualizationProps, VisualizationSettingsDefinitions } from "../../types";

// Treemap Component
export function Treemap(props: VisualizationProps) {
  const { data } = props;
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (chartRef.current) {
      const chart = echarts.init(chartRef.current);

      const rows = data.rows;

      // Build tree: parent (Category A) → children (Category B)
      const treeMapData = [];

      const groupMap = new Map();

      for (const row of rows) {
        const [categoryA, categoryB, value] = row;

        if (!groupMap.has(categoryA)) {
          groupMap.set(categoryA, []);
        }

        groupMap.get(categoryA).push({
          name: categoryB,
          value,
        });
      }

      for (const [parentName, children] of groupMap.entries()) {
        treeMapData.push({
          name: parentName,
          children,
        });
      }

      const option = {
        tooltip: {
          formatter: (info) => {
            const value = info.value;
            const name = info.name;
            return `${name}: ${value}`;
          },
        },
        series: [
          {
            type: "treemap",
            data: treeMapData,
            label: {
              show: true,
              formatter: "{b}",
            },
            upperLabel: {
              show: true,
              height: 30,
            },
            breadcrumb: {
              show: false,
            },
          },
        ],
      };

      chart.setOption(option);

      return () => {
        chart.dispose();
      };
    }
  }, [data]);

  return <div ref={chartRef} style={{ width: "100%", height: "500px" }} />;
}

// Attach Metadata like AreaChart
Object.assign(
  Treemap,
  {
    uiName: t`Treemap`,
    identifier: "treemap",
    iconName: "table",
    noun: t`treemap`,
    minSize: getMinSize("bar"),
    defaultSize: getDefaultSize("bar"),
    settings: {} as VisualizationSettingsDefinitions,
  },
);
